import classNames from 'classnames'
import { FC, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Link } from 'components'
import { ERoutePaths } from 'libraries/router/types'
import { navigationList } from 'utils'
import { useAppSelector } from 'libraries/redux'
import { WishlistSelectors } from 'store/wishlist/selectors'
import { CategoriesSelectors } from 'store/category/selectors'
import { useResponsive } from 'hooks'
import { FacebookIcon, InstagramIcon, PhoneIcon, TelegramIcon, ViberIcon } from 'assets/icons'
import { HearthIcon, SearchIcon, ShoppingCartIcon, CloseIcon, MenuIcon } from 'assets/icons'
import styles from './Header.module.scss'

const Header: FC = () => {
  const navigate = useNavigate()
  const { isTabletL } = useResponsive()

  const { data } = useAppSelector(WishlistSelectors.wishlistList)
  const { data: basketList } = useAppSelector(WishlistSelectors.basketList)
  const { data: storeInfo } = useAppSelector(CategoriesSelectors.storeInfo)

  const [isOpen, setIsOpen] = useState(false)
  const [isSearch, setIsSearch] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const URLfiltering = (url: string, name: string) => {
    if (!url) return ''

    const isFullUrl = url.startsWith('http') || url.startsWith('viber://')

    if (isFullUrl) return url

    switch (name.toLowerCase()) {
      case 'facebook':
        return `https://facebook.com/${url}`
      case 'instagram':
        return `https://instagram.com/${url}`
      case 'telegram':
        return `https://t.me/${url}`
      case 'viber':
        return `viber://add?number=${url}`
      default:
        return url
    }
  }

  const socialUrls = [
    { Icon: FacebookIcon, url: URLfiltering(storeInfo.facebook_url, 'facebook') },
    { Icon: InstagramIcon, url: URLfiltering(storeInfo.instagram_url, 'instagram') },
    { Icon: TelegramIcon, url: URLfiltering(storeInfo.telegram_url, 'telegram') },
    { Icon: ViberIcon, url: URLfiltering(storeInfo.viber_url, 'viber') },
  ]

  const hasSocialUrls = socialUrls?.some(({ url }) => url !== null)

  const trackFbqEvent = (event: 'Contact' | 'Search') => {
    if (storeInfo?.fb_pixel_id) {
      if (typeof window.fbq === 'function') {
        window.fbq('track', event)
      }
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (searchValue.trim().length) {
      navigate(`/products?search=${searchValue}`)
    }
  }

  const renderMenuList = navigationList?.map(({ id, link, text, filter }, index) => (
    <Link
      key={`${id}-${index}`} // ✅ Unique key
      onClick={() => setIsOpen(false)}
      className={styles.wrapper__menu_item}
      navigateTo={link ?? `/products?filter=${filter}`}
    >
      {text}
    </Link>
  ))

  const renderMenuSocList = socialUrls?.map(({ url, Icon }, index) => {
    if (url === null) return null

    const handleContactClick = (index: number) => {
      if (!isTabletL || index === 0) {
        trackFbqEvent('Contact')
      }
    }

    return (
      <a
        className={styles.wrapper_container__menu_list_items_name}
        href={url}
        key={index}
        target='_blank'
        onClick={() => handleContactClick(index)}
      >
        <Icon />
      </a>
    )
  })

  return (
    <>
      {hasSocialUrls && (
        <div className={styles.banner}>
          <p>Завітайте до наших мереж:</p>
          <div className={styles.banner_list_items}>
            {renderMenuSocList}
            <a href={`tel:${storeInfo?.phone?.original}`} className={styles.wrapper_container__menu_list_items_name}>
              <PhoneIcon className={styles.phone} />
            </a>
          </div>
        </div>
      )}

      <header className={styles.container}>
        <div className={classNames(styles.wrapper, 'container')}>
          {isTabletL && <div onClick={() => setIsOpen(!isOpen)}>{isOpen ? <CloseIcon /> : <MenuIcon />}</div>}

          <NavLink to={ERoutePaths.Home} className={styles.wrapper__logo}>
            <p>{storeInfo?.name}</p>
          </NavLink>

          <div className={classNames(styles.wrapper__menu, { [styles.wrapper__menu_active]: isOpen })}>
            <nav className={styles.wrapper__menu_nav}>
              {renderMenuList}
              {isTabletL && (
                <Link
                  onClick={() => setIsOpen(false)}
                  className={styles.wrapper__menu_item}
                  navigateTo={ERoutePaths.Wishlist}
                >
                  Збережене
                </Link>
              )}
            </nav>
            <form
              onSubmit={handleSubmit}
              className={classNames(styles.wrapper__menu_search, { [styles.wrapper__menu_search_active]: isSearch })}
            >
              <div className={styles.wrapper__menu_search_icon}>
                <CloseIcon onClick={() => setIsSearch(false)} />
              </div>

              <input
                onChange={e => {
                  setSearchValue(e.target.value)
                  trackFbqEvent('Search')
                }}
                type='text'
                placeholder='Введіть назву товару або артикул...'
              />
              <button>
                <SearchIcon />
                <span>Знайти</span>
              </button>
            </form>
          </div>

          <div className={styles.wrapper__settings}>
            <NavLink to={ERoutePaths.Wishlist} className={styles.wrapper__settings_item}>
              <HearthIcon />
              {!!data?.length && <span className={styles.wrapper__settings_heart_length}>{data?.length}</span>}
            </NavLink>
            <div onClick={() => setIsSearch(true)} className={styles.wrapper__settings_item}>
              <SearchIcon className={styles.wrapper__settings_item} />
            </div>
            <NavLink to={ERoutePaths.Basket} className={styles.wrapper__settings_item}>
              <ShoppingCartIcon />

              {!!basketList?.length && (
                <span className={styles.wrapper__settings_heart_length}>{basketList?.length}</span>
              )}
            </NavLink>
          </div>
        </div>
      </header>
    </>
  )
}

export default Header
